import { symTol } from './geometry_symmetry.js';

class Dielectric {
    /**
     * Represents a rectangular dielectric region.
     * @param {number} x - Bottom-left corner x-coordinate
     * @param {number} y - Bottom-left corner y-coordinate
     * @param {number} width - Width of the rectangle
     * @param {number} height - Height of the rectangle (can be negative)
     * @param {number} epsilon_r - Relative permittivity
     * @param {number} tan_delta - Loss tangent (default: 0.0)
     * @param {object|null} shape - Optional non-rectangular shape descriptor (see
     *   shapes.js). When set, x/y/width/height are only the bounding box. Containment
     *   tests go through shapeContains() instead. Full-wave backend only. The
     *   rectilinear FDM mesher rasterizes bounding boxes and ignores this.
     */
    constructor(x, y, width, height, epsilon_r, tan_delta = 0.0, shape = null) {
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
        this.epsilon_r = epsilon_r;
        this.tan_delta = tan_delta;
        this.shape = shape;
        // Conductivity in S/m (a doped semiconductor), see conductive_dielectric.js.
        this.sigma = 0;
    }

    get x_min() { return this.x; }
    get x_max() { return this.x + this.width; }
    // Handle negative heights - y_min should always be less than y_max
    get y_min() { return this.height >= 0 ? this.y : this.y + this.height; }
    get y_max() { return this.height >= 0 ? this.y + this.height : this.y; }
}

class Conductor {
    /**
     * Represents a rectangular conductor region.
     * @param {number} x - Bottom-left corner x-coordinate
     * @param {number} y - Bottom-left corner y-coordinate (or top if height is negative)
     * @param {number} width - Width of the rectangle
     * @param {number} height - Height of the rectangle (can be negative for embedded conductors)
     * @param {boolean} is_signal - True for signal conductor, False for ground
     * @param {number} polarity - Signal polarity: +1 (positive), -1 (negative), 0 (ground)
     * @param {object|null} plating - Plating layer config or null
     * @param {number} plating.sigma - Plating conductivity (S/m)
     * @param {number} plating.thickness - Plating thickness (m)
     * @param {number} plating.rq - Plating RMS roughness (m), used for both plating and bulk
     * @param {boolean} plating.top - Apply plating on top face
     * @param {boolean} plating.sides - Apply plating on side faces
     * @param {boolean} plating.bottom - Apply plating on bottom face
     * @param {boolean} plating.all - Apply plating over the whole boundary (shaped
     *   conductors: every edge of the polygon or ring)
     * @param {object|null} shape - Optional non-rectangular shape descriptor (see
     *   shapes.js). When set, x/y/width/height are only the BOUNDING BOX: containment
     *   tests go through shapeContains() instead. Full-wave backend only — the
     *   rectilinear FDM mesher rasterizes bounding boxes and ignores this.
     */
    constructor(x, y, width, height, is_signal = false, polarity = 0, plating = null, shape = null) {
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
        this.is_signal = is_signal;
        this.polarity = is_signal ? (polarity || 1) : 0;
        this.plating = plating;
        this.shape = shape;
    }

    get x_min() { return this.x; }
    get x_max() { return this.x + this.width; }
    // Handle negative heights - y_min should always be less than y_max
    get y_min() { return this.height >= 0 ? this.y : this.y + this.height; }
    get y_max() { return this.height >= 0 ? this.y + this.height : this.y; }
}

class Mesher {
    /**
     * Generates adaptive graded mesh based on conductor and dielectric locations.
     * @param {number} domain_width - Physical domain width
     * @param {number} domain_height - Physical domain height
     * @param {number} nx - Approximate number of mesh points in x-direction
     * @param {number} ny - Approximate number of mesh points in y-direction
     * @param {Array<Conductor>} conductors - List of conductor objects
     * @param {Array<Dielectric>} dielectrics - List of dielectric objects
     * @param {boolean} symmetric - Whether to enforce symmetry (default: false)
     * @param {number} x_min - Minimum x-coordinate (default: 0)
     * @param {number} x_max - Maximum x-coordinate (default: domain_width)
     * @param {number} y_min - Minimum y-coordinate (default: 0)
     * @param {number} y_max - Maximum y-coordinate (default: domain_height)
     * @param {boolean} half_x - Half-domain grid for a mirror-symmetric geometry: the x
     *   mesh is generated for the full symmetric domain (same allocation, grading and
     *   symmetry enforcement as without the flag) and generate_mesh returns only its
     *   x >= centre half, starting exactly on the symmetry plane. The half grid is thus
     *   the exactly right-half of the full grid. Requires symmetric = true.
     */
    constructor(domain_width, domain_height, nx, ny, conductors, dielectrics, symmetric = false, x_min = 0, x_max = null, y_min = 0, y_max = null, half_x = false) {
        this.domain_width = domain_width;
        this.domain_height = domain_height;
        this.nx = nx;
        this.ny = ny;
        this.conductors = conductors;
        this.dielectrics = dielectrics;
        this.symmetric = symmetric;
        this.half_x = half_x;
        this.x_min = x_min;
        this.x_max = x_max !== null ? x_max : domain_width;
        this.y_min = y_min;
        this.y_max = y_max !== null ? y_max : domain_height;

        // Calculate corner mesh parameters
        this.ncorner = Math.max(2, Math.floor(nx / 40));  // About 2-3 lines for nx=100
        this.corner_size = this._min_conductor_dimension() / 10;
    }

    _min_conductor_dimension() {
        let min_dim = Infinity;
        for (const cond of this.conductors) {
            min_dim = Math.min(min_dim, Math.abs(cond.width), Math.abs(cond.height));
        }
        return min_dim !== Infinity ? min_dim : 1e-3;
    }

    _smooth_transition(start, end, n_points, curve_end = 'end', beta = 4.0) {
        if (n_points <= 1) {
            return new Float64Array([start, end]);
        }

        const result = new Float64Array(n_points);
        const tanhBeta = Math.tanh(beta);
        const tanhBetaHalf = Math.tanh(beta * 0.5);

        for (let i = 0; i < n_points; i++) {
            const xi = i / (n_points - 1);
            let eta;

            if (curve_end === 'end') {
                eta = Math.tanh(beta * xi) / tanhBeta;
            } else if (curve_end === 'both') {
                eta = (Math.tanh(beta * (xi - 0.5)) / tanhBetaHalf + 1) / 2;
            } else {  // 'start'
                eta = 1 - Math.tanh(beta * (1 - xi)) / tanhBeta;
            }

            result[i] = start + eta * (end - start);
        }
        return result;
    }

    // Merge interfaces the grid cannot meaningfully separate. Conductor faces and
    // domain walls merge only when numerically coincident (the same physical
    // edge reached through two float paths), so a gap between two conductors,
    // however small, always keeps its own cells. A dielectric interface closer
    // than a thousandth of the smallest conductor dimension to a conductor face
    // or to another dielectric interface (a mask top a few nm from a trace top
    // when t ~ sm thickness) is dropped: a sliver cell there sets the face's H
    // sample and moves R by several percent, while dropping it shifts the
    // dielectric boundary by far less than the discretization error.
    _merge_interfaces(condValues, dielValues, span) {
        const tolExact = Math.max(1e-12, span * 1e-9);
        const tolDiel = Math.max(tolExact, this._min_conductor_dimension() * 1e-3);
        const cond = Array.from(condValues).sort((a, b) => a - b);
        const out = [cond[0]];
        for (let i = 1; i < cond.length; i++) {
            if (cond[i] - out[out.length - 1] > tolExact) out.push(cond[i]);
        }
        for (const v of Array.from(dielValues).sort((a, b) => a - b)) {
            if (out.every(u => Math.abs(u - v) > tolDiel)) out.push(v);
        }
        return out.sort((a, b) => a - b);
    }

    _collect_interfaces(axis) {
        const lo = axis + '_min', hi = axis + '_max';
        const cond_lines = new Set([this[lo], this[hi]]);
        const diel_lines = new Set();

        for (const cond of this.conductors) {
            cond_lines.add(cond[lo]);
            cond_lines.add(cond[hi]);
        }

        for (const diel of this.dielectrics) {
            if (diel[lo] > this[lo]) diel_lines.add(diel[lo]);
            if (diel[hi] < this[hi]) diel_lines.add(diel[hi]);
        }

        return this._merge_interfaces(cond_lines, diel_lines, this[hi] - this[lo]);
    }

    // Mesh density weight of the region [a0, a1] on an axis. y weights conductor
    // interiors higher than x and also raises regions on a dielectric interface.
    _region_weight(axis, a0, a1) {
        const tol = 1e-15;
        const lo = axis + '_min', hi = axis + '_max';
        const isY = axis === 'y';

        // Check if region is inside a conductor
        for (const cond of this.conductors) {
            if (a0 >= cond[lo] - tol && a1 <= cond[hi] + tol) {
                return cond.is_signal ? (isY ? 20.0 : 10.0) : (isY ? 6.0 : 5.0);
            }
        }

        // Minimum conductor dimension along the axis
        let min_dim = Infinity;
        for (const cond of this.conductors) {
            min_dim = Math.min(min_dim, isY ? Math.abs(cond.height) : cond.width);
        }
        if (min_dim === Infinity) {
            min_dim = 1e-3;  // fallback
        }

        // Check if region is near a conductor
        let min_dist = Infinity;
        for (const cond of this.conductors) {
            const dist = Math.min(
                Math.abs(a0 - cond[lo]), Math.abs(a0 - cond[hi]),
                Math.abs(a1 - cond[lo]), Math.abs(a1 - cond[hi])
            );
            min_dist = Math.min(min_dist, dist);
        }

        // Higher base weight at a dielectric interface (y only)
        let mult = 1.0;
        if (isY && this.dielectrics.some(d =>
            Math.abs(a0 - d.y_min) < tol || Math.abs(a0 - d.y_max) < tol ||
            Math.abs(a1 - d.y_min) < tol || Math.abs(a1 - d.y_max) < tol)) {
            mult = 1.5;
        }

        // Weight based on distance relative to conductor dimensions
        if (min_dist < 0.1 * min_dim) {
            return 5.0 * mult;
        } else if (min_dist < 0.25 * min_dim) {
            return 2.5 * mult;
        } else if (min_dist < min_dim) {
            return 1.0 * mult;
        } else {
            return 0.2;
        }
    }

    _mesh_conductor_region(start, end, npts, direction = 'x') {
        const center = (start + end) / 2;
        const length = end - start;

        if (npts < 3) {
            return new Float64Array([start, center, end]);
        }

        const mesh_points = [start, end, center];
        const n_additional = Math.max(0, npts - 3);

        if (n_additional > 0) {
            const n_half = Math.floor(n_additional / 2);

            if (n_half > 0) {
                const left_half_length = length / 2;
                const beta = 2.0;
                const left_points = [];

                for (let i = 1; i <= n_half; i++) {
                    const xi = i / (n_half + 1);
                    const eta = Math.tanh(beta * (xi - 0.5)) / Math.tanh(beta * 0.5) / 2 + 0.5;
                    const pt = start + eta * left_half_length;
                    left_points.push(pt);
                    mesh_points.push(pt);
                    mesh_points.push(2 * center - pt);  // Mirror to right
                }
            }

            if (n_additional % 2 === 1) {
                const offset = length / (4 * npts);
                mesh_points.push(center - offset);
                mesh_points.push(center + offset);
            }
        }

        return Float64Array.from(mesh_points.sort((a, b) => a - b));
    }

    generate_mesh() {
        let x = this._generate_axis_mesh('x');
        const y = this._generate_axis_mesh('y');

        // Validate symmetry if requested
        if (this.symmetric) {
            this._validate_mesh_symmetry(x);
        }

        // Half-domain grid: the x >= centre half of the symmetric full grid, starting
        // exactly on the symmetry plane (_generate_axis_mesh guarantees the centre
        // line of a symmetric x mesh).
        if (this.half_x) {
            if (!this.symmetric) throw new Error('Mesher: half_x requires symmetric meshing');
            const center = (this.x_min + this.x_max) / 2;
            const tol = this.domain_width * 1e-10;
            const k = x.findIndex(v => v > center - tol);
            if (k < 0 || Math.abs(x[k] - center) > tol)
                throw new Error('Mesher: symmetric x mesh has no centre line');
            x = x.slice(k);
            x[0] = center;
        }

        return [x, y];
    }

    _generate_axis_mesh(axis) {
        const interfaces = this._collect_interfaces(axis);
        const base_points = axis === 'x' ? this.nx : this.ny;
        const domain_size = axis === 'x' ? this.domain_width : this.domain_height;
        const weight_func = (a, b) => this._region_weight(axis, a, b);

        const n_regions = interfaces.length - 1;

		// Every region receives at least 5 lines (the signal reservation and
		// the max(5, ...) floor below). A geometry with many thin regions
		// (solder-mask) can have those floors consume the entire per-axis
		// target, leaving zero weight-driven points: far-field region then gets
		// the same 5 lines as a thin band causing large error.
        // When the floors saturate the target, grow the
        // axis budget to 10 lines/region so the weights stay in control, capped
        // at 2x the requested target.
        const n_points = 5 * n_regions >= base_points
            ? Math.min(10 * n_regions, 2 * base_points)
            : base_points;

        // Calculate weights for each region
        const region_weights = [];
        for (let k = 0; k < n_regions; k++) {
            const i0 = interfaces[k];
            const i1 = interfaces[k + 1];
            const width = i1 - i0;
            const weight = weight_func(i0, i1);
            region_weights.push(weight * width);
        }

        // Allocate points. Ensure minimum points in signal conductors
        const MIN_CONDUCTOR_POINTS = 5;
        const region_points = [];
        let reserved_points = 0;
        let non_conductor_weight = 0;

        for (let k = 0; k < n_regions; k++) {
            const i0 = interfaces[k];
            const i1 = interfaces[k + 1];
            let is_signal_conductor = false;

            for (const cond of this.conductors) {
                if (!cond.is_signal) continue;
                const tol = 1e-15;
                const cond_min = axis === 'x' ? cond.x_min : cond.y_min;
                const cond_max = axis === 'x' ? cond.x_max : cond.y_max;

                if (i0 >= cond_min - tol && i1 <= cond_max + tol) {
                    is_signal_conductor = true;
                    break;
                }
            }

            if (is_signal_conductor) {
                region_points.push(MIN_CONDUCTOR_POINTS);
                reserved_points += MIN_CONDUCTOR_POINTS;
            } else {
                region_points.push(0);
                non_conductor_weight += region_weights[k];
            }
        }

        // Allocate remaining points based on weights
        const remaining_points = n_points - reserved_points;
        let allocated = reserved_points;

        for (let k = 0; k < n_regions; k++) {
            if (region_points[k] > 0) continue;

            let pts = non_conductor_weight > 0
                ? Math.max(5, Math.floor(remaining_points * region_weights[k] / non_conductor_weight))
                : 5;
            // The last region takes the rounding remainder, but never less than its
            // own share: the floors of the earlier regions can use up the target,
            // and a far-field region at the end of the axis then got two lines.
            if (k === n_regions - 1) pts = Math.max(pts, n_points - allocated);
            region_points[k] = pts;
            allocated += pts;
        }

        // Generate mesh segments
        const mesh_parts = [];

        for (let k = 0; k < n_regions; k++) {
            const i0 = interfaces[k];
            const i1 = interfaces[k + 1];
            const npts = region_points[k];

            // Check if this region is inside a signal onductor
            let is_signal_conductor = false;
            for (const cond of this.conductors) {
                if (!cond.is_signal) continue;
                const tol = 1e-15;
                const cond_min = axis === 'x' ? cond.x_min : cond.y_min;
                const cond_max = axis === 'x' ? cond.x_max : cond.y_max;

                if (i0 >= cond_min - tol && i1 <= cond_max + tol) {
                    is_signal_conductor = true;
                    break;
                }
            }

            let seg;
            if (is_signal_conductor) {
                seg = this._mesh_conductor_region(i0, i1, npts, axis);
            } else {
                // Determine grading strategy
                let end_curve = 'both';
                let beta_val = 1.0;

                if (Math.abs(i0) < 1e-15) {
                    end_curve = 'end';
                    beta_val = 2.0;
                } else if (Math.abs(i1 - domain_size) < 1e-15) {
                    end_curve = 'start';
                    beta_val = 2.0;
                }

                // Check proximity to conductors
                for (const cond of this.conductors) {
                    const cond_min = axis === 'x' ? cond.x_min : cond.y_min;
                    const cond_max = axis === 'x' ? cond.x_max : cond.y_max;

                    if (Math.abs(i1 - cond_min) < 1e-12) {
                        end_curve = 'end';
                        beta_val = 2.0;
                    } else if (Math.abs(i0 - cond_max) < 1e-12) {
                        end_curve = 'start';
                        beta_val = 2.0;
                    }
                }

                seg = this._smooth_transition(i0, i1, npts, end_curve, beta_val);
            }

            if (k > 0) {
                seg = seg.slice(1);
            }
            mesh_parts.push(seg);
        }

        let mesh = this._concat_arrays(mesh_parts);

        // Ensure exact interface locations
        for (const interface_val of interfaces) {
            let min_dist = Infinity;
            for (const val of mesh) {
                min_dist = Math.min(min_dist, Math.abs(val - interface_val));
            }
            if (min_dist > 1e-12) {
                const temp = Array.from(mesh);
                temp.push(interface_val);
                mesh = Float64Array.from(temp.sort((a, b) => a - b));
            }
        }

        // Add center points for all conductors and dielectrics
        const center_points = [];
        const axis_min_for_centers = axis === 'x' ? this.x_min : 0;
        const axis_max_for_centers = axis === 'x' ? this.x_max : this.y_max;

        for (const cond of this.conductors) {
            const center = axis === 'x' ?
                (cond.x_min + cond.x_max) / 2 :
                (cond.y_min + cond.y_max) / 2;
            if (axis_min_for_centers < center && center < axis_max_for_centers) {
                center_points.push(center);
            }
        }

        for (const diel of this.dielectrics) {
            const center = axis === 'x' ?
                (diel.x_min + diel.x_max) / 2 :
                (diel.y_min + diel.y_max) / 2;
            if (axis_min_for_centers < center && center < axis_max_for_centers) {
                center_points.push(center);
            }
        }

        for (const center of center_points) {
            let min_dist = Infinity;
            for (const val of mesh) {
                min_dist = Math.min(min_dist, Math.abs(val - center));
            }
            if (min_dist > domain_size / 1000) {
                const temp = Array.from(mesh);
                temp.push(center);
                mesh = Float64Array.from(temp.sort((a, b) => a - b));
            }
        }

        // Add boundary lines adjacent to conductor edges
        const boundary_lines = [];
        const axis_min = axis === 'x' ? this.x_min : this.y_min;
        const axis_max = axis === 'x' ? this.x_max : this.y_max;

        for (const cond of this.conductors) {
            const boundary_offset = Math.min(Math.abs(cond.width), Math.abs(cond.height)) / 20;
            const cond_min = axis === 'x' ? cond.x_min : cond.y_min;
            const cond_max = axis === 'x' ? cond.x_max : cond.y_max;
            const edges = [cond_min, cond_max];

            for (const edge of edges) {
                if (Math.abs(edge - axis_min) < 1e-15 || Math.abs(edge - axis_max) < 1e-15) {
                    continue;
                }

                const is_left_edge = Math.abs(edge - cond_min) < 1e-15;
                let outside_line, inside_line;

                if (is_left_edge) {
                    outside_line = edge - boundary_offset;
                    inside_line = edge + boundary_offset;
                } else {
                    inside_line = edge - boundary_offset;
                    outside_line = edge + boundary_offset;
                }

                if (axis_min < outside_line && outside_line < axis_max) {
                    boundary_lines.push([outside_line, boundary_offset]);
                }
                if (cond_min < inside_line && inside_line < cond_max) {
                    boundary_lines.push([inside_line, boundary_offset]);
                }
            }
        }

        // Thin dielectric sheets (solder-mask bands, flagged thin_sheet by the
        // geometry builder) get the same bracket-line treatment as conductor
        // edges. Thick slabs (substrate, top dielectric, stripline cover) are not
        // flagged. The criterion is the sheet's own flag, not its size relative
        // to the conductors, so the grid does not jump when a trace thickness
        // crosses the mask thickness. Faces coincident with conductor surfaces
        // are naturally skipped by the min-distance dedup below.
        for (const diel of this.dielectrics) {
            if (!diel.thin_sheet) continue;
            const dmin = Math.min(Math.abs(diel.x_max - diel.x_min),
                                  Math.abs(diel.y_max - diel.y_min));
            if (dmin <= 0) continue;
            const offset = dmin / 10;
            const lo = axis === 'x' ? Math.min(diel.x_min, diel.x_max) : Math.min(diel.y_min, diel.y_max);
            const hi = axis === 'x' ? Math.max(diel.x_min, diel.x_max) : Math.max(diel.y_min, diel.y_max);
            for (const edge of [lo, hi]) {
                if (Math.abs(edge - axis_min) < 1e-15 || Math.abs(edge - axis_max) < 1e-15) {
                    continue;
                }
                for (const line of [edge - offset, edge + offset]) {
                    if (axis_min < line && line < axis_max) {
                        boundary_lines.push([line, offset]);
                    }
                }
            }
        }

        for (const lines of boundary_lines) {
            const line = lines[0];
            const boundary_offset = lines[1];
            let min_dist = Infinity;
            for (const val of mesh) {
                min_dist = Math.min(min_dist, Math.abs(val - line));
            }
            if (min_dist > boundary_offset / 3) {
                const temp = Array.from(mesh);
                temp.push(line);
                mesh = Float64Array.from(temp.sort((a, b) => a - b));
            }
        }

        // Remove duplicate or near-duplicate points first
        const mesh_unique = [mesh[0]];
        const min_spacing = domain_size * 1e-10;

        for (let i = 1; i < mesh.length; i++) {
            if (mesh[i] - mesh_unique[mesh_unique.length - 1] > min_spacing) {
                mesh_unique.push(mesh[i]);
            }
        }

        mesh = Float64Array.from(mesh_unique);

        // Check symmetry and enforce if needed (do this after all additions)
        if (this.symmetric && axis === 'x') {
            const is_symmetric = this._check_symmetry(axis);
            if (is_symmetric) {
                const axis_min = axis === 'x' ? this.x_min : this.y_min;
                const axis_max = axis === 'x' ? this.x_max : this.y_max;
                // The centre line is the symmetry plane. A half-domain grid (half_x)
                // starts on it and _enforce_symmetry keeps it unpaired. Add it when the
                // generator left it out (e.g. an evenly-counted graded gap region
                // between a differential pair's traces has points at +-d but no 0).
                const center = (axis_min + axis_max) / 2;
                if (!mesh.some(v => Math.abs(v - center) <= min_spacing)) {
                    mesh = Float64Array.from([...mesh, center].sort((a, b) => a - b));
                }
                mesh = this._enforce_symmetry(mesh, axis_min, axis_max);
            } else {
                console.warn('Geometry is not symmetric, but symmetric meshing was requested');
            }
        }

        return mesh;
    }

    _check_symmetry(axis) {
        if (axis !== 'x') return false;

        const center = (this.x_min + this.x_max) / 2;
        const tol = symTol(this.x_max - this.x_min);

        for (const cond of this.conductors) {
            const x_min_mirror = center - (cond.x_max - center);
            const x_max_mirror = center - (cond.x_min - center);

            let found_match = false;
            for (const other of this.conductors) {
                if (Math.abs(other.x_min - x_min_mirror) < tol &&
                    Math.abs(other.x_max - x_max_mirror) < tol &&
                    other.is_signal === cond.is_signal) {
                    found_match = true;
                    break;
                }
            }

            if (Math.abs(cond.x_min + cond.x_max - 2 * center) < tol) {
                found_match = true;
            }

            if (!found_match) {
                return false;
            }
        }

        return true;
    }

    _enforce_symmetry(mesh, axis_min, axis_max) {
        const center = (axis_min + axis_max) / 2;
        const domain_size = axis_max - axis_min;
        const tol = 1e-12;
        const symmetric_mesh = [];
        const used = new Array(mesh.length).fill(false);

        for (let i = 0; i < mesh.length; i++) {
            if (used[i]) continue;

            const point = mesh[i];

            if (Math.abs(point - center) < tol) {
                symmetric_mesh.push(center);
                used[i] = true;
                continue;
            }

            const mirror_pos = 2 * center - point;
            let mirror_idx = null;
            let min_dist = Infinity;

            for (let j = 0; j < mesh.length; j++) {
                if (used[j]) continue;
                // The centre line is its own mirror. Make sure to not delete
                // it.
                if (Math.abs(mesh[j] - center) < tol) continue;
                const dist = Math.abs(mesh[j] - mirror_pos);
                if (dist < min_dist) {
                    min_dist = dist;
                    mirror_idx = j;
                }
            }

            if (mirror_idx !== null && min_dist < domain_size / 100) {
                const avg_pos = center + (point - center);
                const mirror_avg_pos = 2 * center - avg_pos;
                symmetric_mesh.push(avg_pos);
                symmetric_mesh.push(mirror_avg_pos);
                used[i] = true;
                used[mirror_idx] = true;
            } else {
                symmetric_mesh.push(point);
                if (axis_min < mirror_pos && mirror_pos < axis_max) {
                    symmetric_mesh.push(mirror_pos);
                }
                used[i] = true;
            }
        }

        return Float64Array.from(Array.from(new Set(symmetric_mesh)).sort((a, b) => a - b));
    }

    _validate_mesh_symmetry(mesh) {
        const center = (this.x_min + this.x_max) / 2;
        const tol = 1e-10;
        const errors = [];

        // Build a set of mesh points for fast lookup
        const mesh_set = new Set(Array.from(mesh).map(x => x.toFixed(15)));

        for (const point of mesh) {
            const mirror_pos = 2 * center - point;
            const mirror_key = mirror_pos.toFixed(15);

            // Skip the center point itself
            if (Math.abs(point - center) < tol) {
                continue;
            }

            // Check if mirror point exists
            if (!mesh_set.has(mirror_key)) {
                // Check for near matches
                let found_near = false;
                for (const other of mesh) {
                    if (Math.abs(other - mirror_pos) < tol) {
                        found_near = true;
                        break;
                    }
                }

                if (!found_near) {
                    errors.push(`Point ${point} has no symmetric counterpart (expected ${mirror_pos})`);
                }
            }
        }

        if (errors.length > 0) {
            console.error('Mesh symmetry validation failed:');
            errors.forEach(err => console.error('  ' + err));
            throw new Error(`Mesh is not symmetric: ${errors.length} symmetry violations detected`);
        }
    }

    _concat_arrays(arrays) {
        let total_len = 0;
        for (const arr of arrays) {
            total_len += arr.length;
        }
        const result = new Float64Array(total_len);
        let offset = 0;
        for (const arr of arrays) {
            result.set(arr, offset);
            offset += arr.length;
        }
        return result;
    }
}

// Lines of the initial grid on each axis before adaptive refinement, the solver
// default and what the app uses. The adaptive passes place the resolution: a dense
// start spends its lines in the bulk and can exceed the node budget before the first
// refinement.
const DEFAULT_GRID_N = 30;

export { Dielectric, Conductor, Mesher, DEFAULT_GRID_N };
